import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/lib/i18n";
import { AppLayout } from "@/components/Layout/AppLayout";

import Login from "@/pages/Login";
import SADashboard from "@/pages/SuperAdmin/SADashboard";
import Tenants from "@/pages/SuperAdmin/Tenants";
import TenantDetail from "@/pages/SuperAdmin/TenantDetail";
import AuditLog from "@/pages/SuperAdmin/AuditLog";
import TenantDashboard from "@/pages/TenantDashboard";
import InboxPage from "@/pages/Inbox/InboxPage";
import KnowledgePage from "@/pages/Knowledge/KnowledgePage";
import Channels from "@/pages/Settings/Channels";
import Team from "@/pages/Settings/Team";
import AISettings from "@/pages/Settings/AISettings";
import Branding from "@/pages/Settings/Branding";
import Widget from "@/pages/Settings/Widget";
import FeedbackPage from "@/pages/Settings/Feedback";
import WidgetPage from "@/pages/Widget/WidgetPage";

function PrivateRoute({ allowedRoles }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    if (user.role === "superadmin") return <Navigate to="/superadmin" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
}

function RootRedirect() {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role === "superadmin") return <Navigate to="/superadmin" replace />;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <I18nProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/widget/:tenantSlug" element={<WidgetPage />} />

          {/* SuperAdmin Routes */}
          <Route element={<PrivateRoute allowedRoles={["superadmin"]} />}>
            <Route path="/superadmin" element={<AppLayout><SADashboard /></AppLayout>} />
            <Route path="/superadmin/tenants" element={<AppLayout><Tenants /></AppLayout>} />
            <Route path="/superadmin/tenants/:id" element={<AppLayout><TenantDetail /></AppLayout>} />
            <Route path="/superadmin/audit" element={<AppLayout><AuditLog /></AppLayout>} />
            <Route path="/superadmin/feedback" element={<AppLayout><FeedbackPage /></AppLayout>} />
          </Route>

          {/* Tenant Routes — All tenant roles */}
          <Route element={<PrivateRoute allowedRoles={["tenant_admin", "agent", "viewer"]} />}>
            <Route path="/dashboard" element={<AppLayout><TenantDashboard /></AppLayout>} />
            <Route path="/inbox" element={<AppLayout><InboxPage /></AppLayout>} />
            <Route path="/inbox/:id" element={<AppLayout><InboxPage /></AppLayout>} />
            <Route path="/knowledge" element={<AppLayout><KnowledgePage /></AppLayout>} />
          </Route>

          {/* Tenant Admin only routes */}
          <Route element={<PrivateRoute allowedRoles={["tenant_admin"]} />}>
            <Route path="/knowledge" element={<AppLayout><KnowledgePage /></AppLayout>} />
            <Route path="/settings/channels" element={<AppLayout><Channels /></AppLayout>} />
            <Route path="/settings/team" element={<AppLayout><Team /></AppLayout>} />
            <Route path="/settings/ai" element={<AppLayout><AISettings /></AppLayout>} />
            <Route path="/settings/branding" element={<AppLayout><Branding /></AppLayout>} />
            <Route path="/settings/widget" element={<AppLayout><Widget /></AppLayout>} />
            <Route path="/settings/feedback" element={<AppLayout><FeedbackPage /></AppLayout>} />
          </Route>

          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          style: { background: '#121214', border: '1px solid rgba(255,255,255,0.1)', color: '#ffffff' },
          classNames: { title: 'font-heading font-semibold text-sm', description: 'text-xs text-[#a1a1aa]' }
        }}
      />
    </AuthProvider>
    </I18nProvider>
  );
}

export default App;
